import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Mail, 
  Phone, 
  User,
  Edit,
  Trash2,
  FileText,
  ExternalLink
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ProfessionalTerminology } from "@/components/ProfessionalSidebar";

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

interface ClientListProps {
  merchantId: string;
  terminology?: ProfessionalTerminology;
  onClientSelect?: (client: Client) => void;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ClientList({ merchantId, terminology = {}, onClientSelect }: ClientListProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const clientLabel = terminology.customers || "Client";
  const clientsLabel = terminology.customers ? `${terminology.customers}s` : "Clients";

  useEffect(() => {
    fetchClients();
  }, [merchantId]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from("professional_clients")
        .select("*")
        .eq("merchant_id", merchantId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicates = (email: string, phone: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPhone = phone.trim();
    
    const matches: string[] = [];
    clients.forEach((c) => {
      if (normalizedEmail && c.email.toLowerCase() === normalizedEmail) {
        matches.push(`${c.name} (${c.email})`);
      } else if (normalizedPhone && c.phone && c.phone === normalizedPhone) {
        matches.push(`${c.name} (${c.phone})`);
      }
    });
    return matches;
  };

  const handleAddClient = async (forceAdd = false) => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error("Name and email are required");
      return;
    }

    // Check for duplicates unless user already confirmed
    if (!forceAdd) {
      const duplicates = checkDuplicates(formData.email, formData.phone);
      if (duplicates.length > 0) {
        // Check if any duplicate has the exact same name
        const normalizedName = formData.name.trim().toLowerCase();
        const sameNameExists = clients.some(
          (c) =>
            c.name.trim().toLowerCase() === normalizedName &&
            (c.email.toLowerCase() === formData.email.trim().toLowerCase() ||
              (formData.phone.trim() && c.phone === formData.phone.trim()))
        );
        if (sameNameExists) {
          toast.error(`A ${clientLabel.toLowerCase()} with the same name and email/phone already exists. Please use a different name.`);
          return;
        }

        setDuplicateWarning(
          `A ${clientLabel.toLowerCase()} with the same email/phone already exists: ${duplicates.join(", ")}. Do you want to add another person with the same contact info?`
        );
        return;
      }
    } else {
      // Even on force add, block if name is identical
      const normalizedName = formData.name.trim().toLowerCase();
      const sameNameExists = clients.some(
        (c) =>
          c.name.trim().toLowerCase() === normalizedName &&
          (c.email.toLowerCase() === formData.email.trim().toLowerCase() ||
            (formData.phone.trim() && c.phone === formData.phone.trim()))
      );
      if (sameNameExists) {
        toast.error(`A ${clientLabel.toLowerCase()} with the same name and email/phone already exists. Please use a different name.`);
        setDuplicateWarning(null);
        return;
      }
    }

    setDuplicateWarning(null);

    try {
      const { error } = await supabase.from("professional_clients").insert({
        merchant_id: merchantId,
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        notes: formData.notes.trim() || null,
      });

      if (error) throw error;

      toast.success(`${clientLabel} added successfully`);
      setIsAddDialogOpen(false);
      setFormData({ name: "", email: "", phone: "", notes: "" });
      fetchClients();
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Failed to add client");
    }
  };

  const handleEditClient = async () => {
    if (!selectedClient || !formData.name.trim() || !formData.email.trim()) {
      toast.error("Name and email are required");
      return;
    }

    try {
      const { error } = await supabase
        .from("professional_clients")
        .update({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          notes: formData.notes.trim() || null,
        })
        .eq("id", selectedClient.id);

      if (error) throw error;

      toast.success(`${clientLabel} updated successfully`);
      setIsEditDialogOpen(false);
      setSelectedClient(null);
      setFormData({ name: "", email: "", phone: "", notes: "" });
      fetchClients();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error("Failed to update client");
    }
  };

  const handleDeleteClient = async (client: Client) => {
    if (!confirm(`Are you sure you want to delete ${client.name}?`)) return;

    try {
      const { error } = await supabase
        .from("professional_clients")
        .delete()
        .eq("id", client.id);

      if (error) throw error;

      toast.success(`${clientLabel} deleted`);
      fetchClients();
    } catch (error) {
      console.error("Error deleting client:", error);
      toast.error("Failed to delete client");
    }
  };

  const openEditDialog = (client: Client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || "",
      notes: client.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (client.phone && client.phone.includes(searchQuery))
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading {clientsLabel.toLowerCase()}...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>{clientsLabel}</CardTitle>
              <CardDescription>
                Manage your {clientsLabel.toLowerCase()} and their contact information
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setFormData({ name: "", email: "", phone: "", notes: "" });
                setIsAddDialogOpen(true);
              }}
              className="bg-cyan-600 hover:bg-cyan-700 gap-2"
            >
              <Plus className="h-4 w-4" />
              Add {clientLabel}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${clientsLabel.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Client List */}
          {filteredClients.length === 0 ? (
            <div className="text-center py-10">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">
                {searchQuery ? `No ${clientsLabel.toLowerCase()} found` : `No ${clientsLabel.toLowerCase()} yet`}
              </p>
              {!searchQuery && (
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add your first {clientLabel.toLowerCase()}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div 
                    className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
                    onClick={() => onClientSelect?.(client)}
                  >
                    <div className="h-10 w-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{client.name}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1 truncate">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          {client.email}
                        </span>
                        {client.phone && (
                          <span className="flex items-center gap-1 hidden sm:flex">
                            <Phone className="h-3 w-3" />
                            {client.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={client.status === "active" ? "default" : "secondary"}
                      className={client.status === "active" ? "bg-emerald-500" : ""}
                    >
                      {client.status}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(client)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onClientSelect?.(client)}>
                          <FileText className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteClient(client)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New {clientLabel}</DialogTitle>
            <DialogDescription>
              Enter the {clientLabel.toLowerCase()}'s contact information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          {duplicateWarning && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
              <p className="text-sm text-amber-800 dark:text-amber-300">{duplicateWarning}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setDuplicateWarning(null)}>
                  Cancel
                </Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => handleAddClient(true)}>
                  Yes, Add Anyway
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsAddDialogOpen(false); setDuplicateWarning(null); }}>
              Cancel
            </Button>
            <Button onClick={() => handleAddClient()} className="bg-cyan-600 hover:bg-cyan-700">
              Add {clientLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {clientLabel}</DialogTitle>
            <DialogDescription>
              Update the {clientLabel.toLowerCase()}'s information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditClient} className="bg-cyan-600 hover:bg-cyan-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
